// AUTO-SPLIT from the former monolithic index.ts (issue #292).
// Subscription and Pricing types
export type SubscriptionTier = 'trial' | 'free' | 'pro' | 'business' | 'scale' | 'enterprise';

/** Paid capacity bands, ascending by monthly-active-developer headroom. */
export type PaidTier = 'pro' | 'business' | 'scale' | 'enterprise';

/**
 * The paid bands buyable through Stripe Checkout. Enterprise is excluded on
 * purpose: it is sales-led and invoiced, and has no Stripe price ID.
 */
export type SelfServePaidTier = 'pro' | 'business' | 'scale';

/**
 * Runtime list of the paid bands, ascending. Kept beside the types so "is this
 * org paying?" has one answer: a hardcoded ['pro','business'] check treated
 * paying Scale and Enterprise orgs as unpaid.
 */
export const PAID_TIERS: readonly PaidTier[] = ['pro', 'business', 'scale', 'enterprise'];

export type SubscriptionStatus = 'active' | 'trialing' | 'past_due' | 'canceled' | 'unpaid' | 'paused' | 'expired';

// Backend trial lifecycle state machine (Section A). The frontend never
// re-derives trial dates - all trial UI reads this state from the API.
export type TrialState = 'none' | 'active' | 'ending_soon' | 'expired_grace' | 'lapsed' | 'converted';


export interface SubscriptionLimits {
  max_developers: number | null;
  current_developers: number;
  current_mau: number;
  all_time_developers: number;
  active_window_days: number;
  max_organizations: number | null;
  max_alert_rules: number | null;
  retention_days: number;
}


export interface SubscriptionInfo {
  id: string;
  organization_id: string;
  tier: SubscriptionTier;
  effective_tier: SubscriptionTier;
  status: SubscriptionStatus;
  is_trial: boolean;
  is_trial_active: boolean;
  trial_state: TrialState;
  trial_days_remaining: number | null;
  // Emailed trial coupon code (e.g. "TRIAL20"). NOT yet part of the Phase 2a
  // response contract - optional on purpose. Phase 3 (Section D) wires the
  // backend field; until then absence means "link to /pricing without promo".
  discount_code_sent?: string | null;
  trial_ends_at: string | null;
  limits: SubscriptionLimits;
  features: string[];
  premium_features_locked: string[];
  stripe_customer_id: string | null;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
  is_paused: boolean;
  paused_at: string | null;
  pause_ends_at: string | null;
  created_at: string;
  updated_at: string;
}


export interface TierLimits {
  max_developers: number | null;
  max_organizations: number | null;
  retention_days: number;
  max_alert_rules: number | null;
  features: string[];
}


export interface TierPricing {
  name: string;
  price_monthly: number;
  price_yearly: number;
  /**
   * Whether this tier can be bought through Stripe Checkout. Enterprise is
   * sales-led, so its prices are a published "from" anchor and its CTA must
   * route to contact-sales rather than Checkout.
   */
  self_serve: boolean;
  limits: TierLimits;
}


/**
 * Paid tiers are flat-priced capacity bands over monthly-active developers,
 * ascending by headroom: pro -> business -> scale -> enterprise.
 */
export interface PricingTiers {
  free: TierPricing;
  pro: TierPricing;
  business: TierPricing;
  scale: TierPricing;
  enterprise: TierPricing;
}


export interface FeatureCheckResponse {
  feature: string;
  has_access: boolean;
  current_tier: string;
  is_premium: boolean;
}


export interface DeveloperLimitResponse {
  current_mau: number;
  all_time: number;
  max: number | null;
  can_add: boolean;
  tier: string;
  active_window_days: number;
}


// Premium features list
export const PREMIUM_FEATURES = [
  'file_hotspots',
  'knowledge_silos',
  'risky_changes',
  'review_network',
  'executive_summary',
  'performance_insights',
  'alerts',
  'csv_exports',
  'sentiment_analysis',
  'forecasting',
  'deployment_analytics',
  'ship_log'
] as const;


export type PremiumFeature = typeof PREMIUM_FEATURES[number];


// ==========================================
// Billing & Retention Types
// ==========================================

export interface UsageSummary {
  days_as_customer: number;
  total_prs_analyzed: number;
  total_developers: number;
  total_alerts_fired: number;
  knowledge_silos_found: number;
  risky_changes_flagged: number;
  features_used: string[];
  tier: string;
  months_subscribed: number;
}


export interface FeatureWillLose {
  name: string;
  usage_count: number | null;
}


export interface CancellationContext {
  usage_summary: UsageSummary;
  features_will_lose: FeatureWillLose[];
  current_tier: string;
  current_period_end: string | null;
  data_retention_days: number;
}


export interface CancellationFeedbackRequest {
  primary_reason: string;
  secondary_reasons?: string[];
  competitor_name?: string;
  missing_features?: string[];
  feedback_text?: string;
  satisfaction_score?: number;
}


export interface InterventionContent {
  title: string;
  message: string;
  tips: string[];
  help_link: string | null;
  cta: string;
}


export interface RetentionOffer {
  type: string;
  title: string;
  description: string;
  savings: string;
  terms: string;
}


export interface CancellationFeedbackResponse {
  feedback_id: string;
  intervention: InterventionContent;
  recommended_offers: RetentionOffer[];
  usage_summary: UsageSummary;
}


export interface AcceptOfferResponse {
  offer_id: string;
  type: string;
  coupon_id?: string;
  discount_percent?: number;
  duration_months?: number;
  credit_applied_cents?: number;
  pause_until?: string;
  new_trial_ends_at?: string;
  message?: string;
  calendar_link?: string;
}


export interface PauseSubscriptionResponse {
  status: string;
  paused_until: string;
  subscription_id: string;
}


export interface Invoice {
  id: string;
  number: string | null;
  amount_due: number;
  amount_paid: number;
  currency: string;
  status: string;
  created: number;
  period_start: number;
  period_end: number;
  hosted_invoice_url: string | null;
  invoice_pdf: string | null;
}


export interface PaymentMethod {
  id: string;
  type: string;
  card?: {
    brand: string;
    last4: string;
    exp_month: number;
    exp_year: number;
  };
}


export interface CompleteCancellationResponse {
  status: string;
  feedback_id: string;
  at_period_end: boolean;
}


export type CancellationReason =
  | 'not_using_enough'
  | 'too_expensive'
  | 'missing_features'
  | 'team_smaller'
  | 'switching_competitor'
  | 'confusing_hard_to_use'
  | 'team_didnt_adopt'
  | 'just_testing'
  | 'budget_cut'
  | 'other';


export const CANCELLATION_REASONS: { value: CancellationReason; label: string }[] = [
  { value: 'not_using_enough', label: "Not using it enough" },
  { value: 'too_expensive', label: "Too expensive" },
  { value: 'missing_features', label: "Missing features I need" },
  { value: 'team_smaller', label: "Team got smaller" },
  { value: 'switching_competitor', label: "Switching to another tool" },
  { value: 'confusing_hard_to_use', label: "Too confusing or hard to use" },
  { value: 'team_didnt_adopt', label: "Team didn't adopt it" },
  { value: 'just_testing', label: "Just testing, wasn't ready to buy" },
  { value: 'budget_cut', label: "Budget cut / company changes" },
  { value: 'other', label: "Other reason" },
];
